(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const gallerySelector = '#MainContent[data-template^="product"] .product-information__media media-gallery.media-gallery--hint > slideshow-component';
  /* A swipe changes card when it travels this far... */
  const SWIPE_DISTANCE = 35;
  /* ...or when it is a quick flick: shorter, but fast enough (px per ms). */
  const FLICK_DISTANCE = 12;
  const FLICK_VELOCITY = 0.35;
  /* Bottom strip of a <video> where the native controls (seek bar) live. */
  const VIDEO_CONTROLS_ZONE = 72;
  let gesture = null;

  let settle = null;

  const finish = (targetIndex) => {
    const start = gesture;
    gesture = null;
    if (!start || !start.scroller.isConnected) return;

    const { slideshow, scroller, initialIndex, positions } = start;
    if (start.dragFrame) {
      cancelAnimationFrame(start.dragFrame);
      scroller.scrollLeft = start.pendingScroll;
      start.dragFrame = 0;
    }
    const index = targetIndex == null ? initialIndex : targetIndex;
    const destination = positions[index];
    if (!Number.isFinite(destination)) return;
    const from = scroller.scrollLeft;
    let frame = 0;
    const commit = () => {
      cancelAnimationFrame(frame);
      if (scroller.isConnected) {
        scroller.scrollLeft = destination;
        slideshow.current = index;
        scroller.style.scrollSnapType = start.snapType;
        scroller.style.scrollBehavior = start.scrollBehavior;
      }
      if (settle?.cancel === cancel) settle = null;
    };
    const cancel = () => cancelAnimationFrame(frame);

    if (Math.abs(destination - from) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      commit();
      return;
    }

    let began = null;
    const tick = (time) => {
      if (!scroller.isConnected) {
        if (settle?.cancel === cancel) settle = null;
        return;
      }
      if (began === null) began = time;
      const progress = Math.min((time - began) / 220, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      scroller.scrollLeft = from + (destination - from) * eased;
      if (progress < 1) frame = requestAnimationFrame(tick);
      else commit();
    };
    settle = { cancel, targetIndex: index, snapType: start.snapType, scrollBehavior: start.scrollBehavior };
    frame = requestAnimationFrame(tick);
  };
  document.addEventListener('touchstart', (event) => {
    /* A second finger (or any new touch) while a swipe is in progress: settle the gallery back on
       its card. Dropping the gesture here used to leave it stuck between two cards with
       scroll-snap switched off. */
    if (gesture) finish(null);
    gesture = null;
    if (!mobile.matches || event.touches.length !== 1) return;

    const target = event.target;
    const touch = event.touches[0];
    const slideshow = document.querySelector(gallerySelector);
    if (!(target instanceof Element) || !slideshow || !Number.isInteger(slideshow.current)) return;
    /* Nothing behind an open layer may move. The mobile menu is a <details> drawer, not a dialog,
       so it is named here as well (a swipe on the open menu used to change the product picture
       behind it). */
    if (document.querySelector('dialog[open], [role="dialog"][aria-modal="true"], .menu-drawer-container[open]')) return;
    if (target.closest('input, textarea, select, iframe, model-viewer, [contenteditable="true"]')) return;

    /* Video cards can be swiped like image cards (native horizontal panning is off on the rail, so
       without this the card could not be left by touching it). Only the controls strip of a video
       with native controls is left alone, so the seek bar keeps working. 3D models keep their own
       drag. */
    const video = target.closest('video');
    if (video && video.controls) {
      const videoRect = video.getBoundingClientRect();
      if (touch.clientY > videoRect.bottom - VIDEO_CONTROLS_ZONE) return;
    }
    const mediaSlide = target.closest('.product-media-container');
    if (mediaSlide && mediaSlide.classList.contains('product-media-container--model')) return;

    const otherSlideshow = target.closest('slideshow-component');
    if (otherSlideshow && otherSlideshow !== slideshow) return;
    if (target.closest('button, a') && !target.closest('.product-media-container')) return;

    const scroller = slideshow.querySelector(':scope > slideshow-container > slideshow-slides');
    const slides = slideshow.slides;
    if (!scroller || !slides || slides.length < 2) return;

    const previousSettle = settle;
    if (previousSettle) {
      previousSettle.cancel();
      settle = null;
    }
    const initialScroll = scroller.scrollLeft;
    const scrollerRect = scroller.getBoundingClientRect();
    const positions = slides.map((slide) => {
      const rect = slide.getBoundingClientRect();
      return initialScroll + rect.left - scrollerRect.left - (scrollerRect.width - rect.width) / 2;
    });
    const initialIndex = previousSettle?.targetIndex ?? positions.reduce((best, position, index) =>
      Math.abs(position - initialScroll) < Math.abs(positions[best] - initialScroll) ? index : best, 0);
    gesture = {
      identifier: touch.identifier, x: touch.clientX, y: touch.clientY,
      lastX: touch.clientX, lastTime: event.timeStamp, velocity: 0,
      initialScroll, initialIndex, positions,
      slideshow, scroller, dragging: false, dragFrame: 0, pendingScroll: initialScroll,
      snapType: previousSettle?.snapType ?? scroller.style.scrollSnapType,
      scrollBehavior: previousSettle?.scrollBehavior ?? scroller.style.scrollBehavior
    };
  }, { passive: true });

  document.addEventListener('touchmove', (event) => {
    const start = gesture;
    if (!start) return;
    if (event.touches.length !== 1 || !start.scroller.isConnected) {
      finish(null);
      return;
    }
    const touch = event.touches[0];
    if (touch.identifier !== start.identifier) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (!start.dragging) {
      if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
        finish(null);
        return;
      }
      if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
      /* The browser is already scrolling the page with this touch and will not let go of it:
         moving the gallery too would make both move at once (diagonal judder). */
      if (!event.cancelable) {
        finish(null);
        return;
      }
      start.dragging = true;
      start.scroller.style.scrollSnapType = 'none';
      start.scroller.style.scrollBehavior = 'auto';
    }

    if (event.cancelable) event.preventDefault();

    /* Smoothed horizontal speed of the finger, for flick detection on release. */
    const elapsed = event.timeStamp - start.lastTime;
    if (elapsed > 0) {
      const instant = (touch.clientX - start.lastX) / elapsed;
      start.velocity = start.velocity * 0.6 + instant * 0.4;
      start.lastX = touch.clientX;
      start.lastTime = event.timeStamp;
    }

    const neighboring = [start.positions[start.initialIndex - 1], start.positions[start.initialIndex + 1]]
      .filter(Number.isFinite);
    const low = Math.min(start.positions[start.initialIndex], ...neighboring);
    const high = Math.max(start.positions[start.initialIndex], ...neighboring);
    start.pendingScroll = Math.max(low, Math.min(high, start.initialScroll - dx));
    if (!start.dragFrame) {
      start.dragFrame = requestAnimationFrame(() => {
        start.dragFrame = 0;
        if (gesture === start && start.scroller.isConnected) {
          start.scroller.scrollLeft = start.pendingScroll;
        }
      });
    }
  }, { passive: false });

  document.addEventListener('touchend', (event) => {
    const start = gesture;
    if (!start || event.touches.length) return;
    const touch = Array.from(event.changedTouches).find((item) => item.identifier === start.identifier);
    if (!touch) {
      finish(null);
      return;
    }

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    /* A finger that rested before lifting is not a flick. */
    const velocity = event.timeStamp - start.lastTime > 120 ? 0 : start.velocity;
    const flick = Math.abs(dx) >= FLICK_DISTANCE &&
      Math.abs(velocity) >= FLICK_VELOCITY &&
      Math.sign(velocity) === Math.sign(dx);
    if (!start.dragging || Math.abs(dx) < Math.abs(dy) * 1.2 || (Math.abs(dx) < SWIPE_DISTANCE && !flick)) {
      finish(null);
      return;
    }

    const rtl = document.documentElement.dir === 'rtl';
    const next = rtl ? dx > 0 : dx < 0;
    const index = Math.max(0, Math.min(start.slideshow.slides.length - 1, start.initialIndex + (next ? 1 : -1)));
    finish(index);
  }, { passive: true });

  document.addEventListener('touchcancel', () => { finish(null); }, { passive: true });
})();
